/**
 * CommunityMemberProfile — privacy-safe profile opened from a Community Feed
 * @mention. Accessible to EVERY feed participant (talent / agency / client /
 * admin), unlike the client-facing Discover profile which blocks peers.
 *
 * Deleted / deactivated members render a graceful "no longer available"
 * message instead of a dead "Profile not found" screen.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Star, CheckCircle2, Sparkles, UserX } from 'lucide-react';
import api from '../utils/api';
import { resolveProfilePhoto, resolveInitials } from '../utils/profilePhoto';

export default function CommunityMemberProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    setLoading(true);
    api.get(`/feed/member/${userId}`)
      .then((r) => { if (m) setData(r.data); })
      .catch(() => { if (m) setData({ available: false, reason: 'not_found' }); })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <Loader2 className="w-7 h-7 animate-spin text-cyan-600" />
      </div>
    );
  }

  const unavailable = !data || data.available === false;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="community-member-profile">
      <div className="max-w-2xl mx-auto px-4 py-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"
          data-testid="community-member-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {unavailable ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="community-member-unavailable">
            <span className="w-14 h-14 rounded-full bg-slate-100 text-slate-400 grid place-items-center mx-auto mb-3">
              <UserX className="w-7 h-7" />
            </span>
            <h2 className="text-lg font-bold text-slate-800">This profile is no longer available</h2>
            <p className="text-sm text-slate-500 mt-1">
              {data?.reason === 'deactivated'
                ? 'This member has deactivated their account.'
                : 'We couldn’t find this member — they may have left iEvntür.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="h-20 bg-gradient-to-r from-cyan-600 to-teal-500" />
            <div className="px-5 pb-6 -mt-10">
              <div className="flex items-end gap-4">
                {(() => {
                  const photoUrl = resolveProfilePhoto({ profile_photo_url: data.photo });
                  return photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={data.name}
                      className="w-20 h-20 rounded-2xl border-4 border-white object-cover bg-slate-100"
                      data-testid="community-member-photo"
                    />
                  ) : (
                    <div
                      className="w-20 h-20 rounded-2xl border-4 border-white bg-cyan-100 text-cyan-800 grid place-items-center text-2xl font-bold"
                      data-testid="community-member-photo"
                    >
                      {resolveInitials({ display_name: data.name })}
                    </div>
                  );
                })()}
                <div className="pb-1 min-w-0">
                  <h1 className="text-xl font-bold text-slate-900 truncate flex items-center gap-2" data-testid="community-member-name">
                    {data.name}
                    {data.is_featured && <Sparkles className="w-4 h-4 text-amber-500" title="Featured" />}
                  </h1>
                  {data.role_name && (
                    <p className="text-sm text-slate-500">{data.role_name}{data.tier ? ` · ${data.tier}` : ''}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                {(data.city || data.state) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {[data.city, data.state].filter(Boolean).join(', ')}
                  </span>
                )}
                {data.stats?.average_rating != null && (data.stats?.total_ratings || 0) > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    {data.stats.average_rating.toFixed(1)}
                    <span className="text-slate-400">({data.stats.total_ratings})</span>
                  </span>
                )}
                {data.is_creator && (
                  <span className="inline-flex items-center gap-1 text-cyan-700 font-semibold">
                    <CheckCircle2 className="w-4 h-4" /> Creator
                  </span>
                )}
              </div>

              {data.bio && (
                <p className="mt-4 text-sm text-slate-700 whitespace-pre-line" data-testid="community-member-bio">{data.bio}</p>
              )}

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  <div className="text-[10px] uppercase font-bold tracking-wide text-slate-400">Shifts Completed</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="community-member-shifts">{data.stats?.shifts_completed ?? 0}</div>
                </div>
                <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  <div className="text-[10px] uppercase font-bold tracking-wide text-slate-400">Member Since</div>
                  <div className="text-lg font-bold text-slate-900">
                    {data.member_since ? new Date(data.member_since).getFullYear() : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
